import React, { createRef, useEffect, useRef, useState } from 'react'
import { nominationThunks } from '@/entity/nomination'
import { useDispatch, useSelector } from 'react-redux'
import cl from 'classnames'
import { Modal } from '../Modal'
import { NominationHeader } from '@features/nomination/NominationHeader'
import { AntdTable } from '@/components/AntdTable/AntdTable'
import { useTranslation } from 'react-i18next'
import { getCurrentLanguage } from '@/i18next'
import { LANGUAGE_ENUM } from '@/shared'
import { Button, Field, Radio } from '@/ui'
import { Field as FormikField, Formik, Form } from 'formik'
import { DynamicField } from '@/components/DynamicField'
import { LoaderWrapper } from '@/components/Loader'
import { DigitalSign } from '@/utils/digitalSign'
import { message } from 'antd'
import { NOMINATION_STATUSES, generateData } from '@/entity/nomination'
import { ExpandableText } from '@/components/ExpandableText/ExpandableText'
import { InfoGreen } from '@app/icons'
import { Alert } from '@/components/Alert/Alert'
import { DownloadLink } from '@features/nomination/DownloadLink'

const RADIO_VALUES = {
	APPROVED: 'Approved',
	NOT_APPROVED: 'NotApproved',
}

export const ViewModal = ({ item, onClose, onSuccess }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const lang = getCurrentLanguage()
	const digitalSign = new DigitalSign()

	const [data, setData] = useState([])
	const [rejectNote, setRejectNote] = useState(item?.mioNote || '')

	const { dictValues, mioCheckRow } = useSelector((state) => ({
		dictValues: state.nomination.dictValues,
		mioCheckRow: state.nomination.mioCheckRow,
	}))

	const radioOptions = [
		{
			code: RADIO_VALUES.APPROVED,
			nameRu: t('approved'),
			nameKz: t('approved'),
		},
		{
			code: RADIO_VALUES.NOT_APPROVED,
			nameRu: t('not_approved'),
			nameKz: t('not_approved'),
		},
	]

	useEffect(() => {
		let body = {
			data: { nominationCode: item?.nomination?.code },
			onSuccess: (res) => {
				let params = generateData(item?.paramValues, res, dictValues?.data)
				let sorted = params?.sort((a, b) => a?.orderNum > b?.orderNum ? 1 : -1)
				setData(sorted)
			},
		}

		dispatch(nominationThunks.getDictParams(body))
	}, [])

	const columns = [
		{
			key: 'criterion',
			dataIndex: 'title',
			title: t('nomination.criterion'),
		},
		{
			key: 'employer_data',
			dataIndex: 'employer_data',
			title: t('employer_data'),
			width: 250,
			render: (item, record, index) => {
				return (
					<div className={cl('flex', 'flex-column', 'gap-3')}>
						<ExpandableText>
							{item || '-'}
						</ExpandableText>

						{record.employer_file && <DownloadLink id={record.employer_file}/>}
					</div>
				)
			}
		},
		{
			key: 'comp_data',
			dataIndex: 'comp_data',
			title: t('nomination.is_data'),
			width: 180,
			render: (item, record, index) => {
				return (
					<div className={cl('flex', 'flex-column', 'gap-3')}>
						<ExpandableText>
							{item || '-'}
						</ExpandableText>

						{record.comp_file && <DownloadLink id={record.comp_file}/>}
					</div>
				)
			}
		},
		...(item?.status === NOMINATION_STATUSES.Computed ? [{
			key: 'action',
			dataIndex: 'action',
			title: '',
			width: 180,
			render: (item, record, index) => {
				return (
					record.hasEdit &&
					<Radio options={radioOptions} value={record.mioStatus} name={record.paramCode}
								 onChange={(e) => handleChangeRadio(e, index)} />
				)
			},
		}] : [
			{
				key: 'mio_data',
				dataIndex: 'mio_data',
				title: t('nomination.mio_data'),
				width: 180,
				render: (item, record, index) => {
					return (
						<div className={cl('flex', 'flex-column', 'gap-3')}>
							<ExpandableText>
								{item || '-'}
							</ExpandableText>

							{record.mio_file && <DownloadLink id={record.mio_file}/>}
						</div>
					)
				}
			},
			{
				key: 'mioComment',
				dataIndex: 'mioComment',
				title: t('staff_modal.comment'),
				width: 180,
				render: (item, record, index) => {
					return (
						<ExpandableText>
							{item || '-'}
						</ExpandableText>
					)
				}
			}
		]),
	]

	const handleChangeRadio = (e, idx) => {
		const { value } = e.target

		if (value === RADIO_VALUES.APPROVED) {
			let res = {
				mioComment: null,
				mioStatus: value,
				paramCode: data[idx]?.paramCode,
			}
			handleSaveRow(res, idx)
		} else {
			setData(prevState => {
				const newState = [...prevState]
				newState[idx] = { ...newState[idx], mioStatus: value, isEdit: !!newState[idx].mioComment }
				return newState
			})
		}
	}

	const handleSaveRow = (data, idx) => {
		let body = {
			data: data,
			payload: {
				externalId: item?.externalId,
			},
			onSuccess: (res) => {
				setData(prevState => {
					const newState = [...prevState]
					newState[idx] = { ...newState[idx], ...data, isEdit: true }
					return newState
				})
			},
		}

		dispatch(nominationThunks.setMioRow(body))
	}

	const handleEditRow = (idx, value) => {
		setData(prevState => {
			const newState = [...prevState]
			newState[idx] = { ...newState[idx], isEdit: value }
			return newState
		})
	}

	const handleSign = async (xmlData) => {
		const xml = await digitalSign.handleSend(xmlData)

		let xmlBody = {
			data: {
				xmlData: xml,
			},
			onSuccess: (res) => {
				onSuccess()
				message.success(t('reviews.success_payload'))
			},
		}

		dispatch(nominationThunks.setMioSign(xmlBody))
	}

	const handleXmlSign = () => {
		let body = {
			data: item?.externalId,
			onSuccess: async ({ xmlData }) => {
				await handleSign(xmlData)
			},
		}
		dispatch(nominationThunks.setMioXml(body))
	}

	const handleXmlReject = () => {
		let body = {
			data: {
				noteReject: rejectNote,
			},
			payload: {
				externalId: item?.externalId,
			},
			onSuccess: async ({ xmlData }) => {
				await handleSign(xmlData)
			},
		}
		dispatch(nominationThunks.setRejectMioXml(body))
	}

	const rejectedButtonDisabled = item?.status === NOMINATION_STATUSES.Computed ? !rejectNote : true
	const signButtonDisabled = item?.status === NOMINATION_STATUSES.Computed ? rejectNote : true

	return (
		<Modal onClose={onClose}>
			<NominationHeader item={item} />

			<div className={cl('my-4')}>
				<Alert text={t('nomination.mio_alert')} icon={<InfoGreen />} />
			</div>

			<div className={cl('my-4')}>
				<LoaderWrapper isLoading={mioCheckRow.loading}>
					<AntdTable
						columns={columns}
						dataSource={data}
						pagination={false}
						rowKey={record => record.key}
						showExpandColumn={false}
						expandable={{
							expandedRowKeys: item?.status !== NOMINATION_STATUSES.Computed ? [] : data?.filter(i => i.mioStatus === RADIO_VALUES.NOT_APPROVED)?.map(i => i.key),
							expandedRowRender: (record, index, indent, expanded) => {
								let dynamicFieldName = `mio${record.dict.localParamType[0].toUpperCase() + record.dict.localParamType.slice(1)}`

								let placeholder = {
									[LANGUAGE_ENUM.kz]: record.dict.noteKz,
									[LANGUAGE_ENUM.rus]: record.dict.noteRu,
								}

								let label = {
									[LANGUAGE_ENUM.kz]: record.dict.nameKz,
									[LANGUAGE_ENUM.rus]: record.dict.nameRu,
								}

								let hint = {
									[LANGUAGE_ENUM.kz]: record.dict.infoKz,
									[LANGUAGE_ENUM.rus]: record.dict.infoRu,
								}

								let initialValues = {
									mioComment: record.mioComment,
									mioStatus: record.mioStatus,
									paramCode: record.paramCode,
									[dynamicFieldName]: record?.[dynamicFieldName],
								}

								return (
									<Formik initialValues={initialValues}
													onSubmit={{}}
													enableReinitialize
									>
										{({ values, errors }) => {
											return (
												<Form>
													<div className={cl('flex', 'gap-4', 'flex-column')}>
														<div className={cl('flex-1')}>
															<FormikField name="mioComment">
																{({ field, meta: { touched, error } }) => {
																	return (
																		<Field
																			{...field}
																			isRequired={true}
																			label={t('staff_modal.comment')}
																			placeholder={t('staff_modal.comment_placeholder')}
																			error={error}
																			disabled={record.isEdit}
																		/>
																	)
																}}
															</FormikField>
														</div>
														<div className={cl('flex-1')}>
															<FormikField name={dynamicFieldName}>
																{({ field, meta: { touched, error } }) => {
																	return (
																		<DynamicField
																			{...field}
																			isRequired={true}
																			placeholder={placeholder[lang] || ''}
																			label={label[lang] || ''}
																			type={record.dict.paramType}
																			hint={hint[lang] || ''}
																			options={dictValues?.data?.[record.dict.dictCode]}
																			error={touched && error}
																			disabled={record.isEdit}
																		/>
																	)
																}}
															</FormikField>

														</div>

														<div>
															{
																item?.status === NOMINATION_STATUSES.Computed &&
																(record.isEdit ?
																	<Button onClick={() => handleEditRow(index, false)}>
																		{t('edit')}
																	</Button>
																	:
																	<div className={cl('flex', 'gap-4')}>
																		<Button onClick={() => handleSaveRow(values, index)}>
																			{t('save')}
																		</Button>
																	</div>)
															}
														</div>
													</div>
												</Form>
											)
										}}
									</Formik>
								)
							},
						}}
					/>
				</LoaderWrapper>
			</div>

			<div className={cl('my-4')}>
				<Field
					label={t('nomination.reason_decline')}
					placeholder={t('nomination.reason_decline_placeholder')}
					fieldType="textarea"
					showCount
					maxLength={4000}
					rows={6}
					hint={t('nomination.reason_decline_hint')}
					value={rejectNote}
					onChange={(value) => setRejectNote(value)}
					disabled={item?.status !== NOMINATION_STATUSES.Computed}
				/>
			</div>

			<div className={cl('flex', 'gap-4', 'justify-content-end')}>
				<Button onClick={handleXmlReject} backgroundColor="white" textColor="default" borderColor="default" border
								disabled={rejectedButtonDisabled}>
					{t('decline')}
				</Button>

				<Button onClick={handleXmlSign} disabled={signButtonDisabled}>
					{t('nomination.send_mtszn')}
				</Button>
			</div>
		</Modal>
	)
}